<template>
  <div
    v-if="tabItem.tabPanel.label && displayLabel"
    class="tab-panel-label"
  >
    {{ t(tabItem.tabPanel.label) }}
  </div>
  <div
    v-if="!isSynchronizedPage && structureActions.length"
    class="flex justify-content-end gap-2 mt-2 mb-2"
  >
    <SpmButton
      v-for="(action, index) of structureActions"
      :key="index"
      v-tooltip="action.label"
      class="p-button p-button-secondary"
      :icon="action.icon"
      @click="action.command"
    />
    <HelpMessage
      v-tooltip="t('showHelpArticle')"
      class="flex align-items-center"
      :link="{
        type: 'gleap',
        articleId: '166',
        label: ''
      }"
    />
  </div>
  <div
    v-if="isSynchronizedPage && structureActions.length && !selectedSyncElement"
    class="flex justify-content-end gap-2 mt-2 mb-2"
  >
    <SpmButton
      v-tooltip="t('templateBuilder.structureActions.page.editDesign')"
      class="p-button p-button-secondary"
      icon="far fa-pen-to-square"
      @click="enterEditSyncElement"
    />
  </div>
  <ul v-if="tabItem.tabPanel.items">
    <template
      v-for="(fieldGroup, fieldGroupIndex) of tabItems"
      :key="fieldGroup.id"
    >
      <ConfigurationPanelTabContentItem
        :tab-item="tabItem"
        :parser-values="parserValues"
        :field-group="fieldGroup"
        :field-group-index="fieldGroupIndex"
        :active-field-group-index="activeFieldGroupIndex"
        :is-synchronized-page="isSynchronizedPage"
        :header-title="headerTitle"
        @refresh="handleRefresh"
        @toggle-field-group="toggleFieldGroup"
      />
    </template>
  </ul>
</template>

<script lang="ts">
import {
  defineComponent, onMounted,
  PropType, Ref,
  ref,
  watch,
  onBeforeUnmount,
  computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';

import {
  TabItem,
  TabSubitem,
  ParserObject,
  TemplateStructureEnum,
} from '@/types';

import Tooltip from 'primevue/tooltip';

import ConfigurationPanelTabContentItem from '@/components/template-builder/panels/ConfigurationPanelTabContentItem.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';

export default defineComponent({
  name: 'ConfigurationPanelTabContent',

  components: {
    ConfigurationPanelTabContentItem,
    SpmButton,
    HelpMessage,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    tabItem: {
      type: Object as PropType<TabItem | TabSubitem>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserObject>,
      required: true,
    },

    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },

    headerTitle: {
      type: String,
      required: false,
      default: '',
    },

    isSynchronizedPage: {
      type: Boolean,
      required: false,
      default: false,
    },

    structureActions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  emits: ['refresh'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const activeFieldGroupIndex: Ref<number[]> = ref([]);
    const toggleFieldGroup = (index: number) => {
      if (activeFieldGroupIndex.value.includes(index)) {
        activeFieldGroupIndex.value.splice(activeFieldGroupIndex.value.indexOf(index), 1);
      } else {
        activeFieldGroupIndex.value.push(index);
      }
    };
    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);

    const tabItems = computed(() => {
      if (props.tabItem.tabPanel && props.tabItem.tabPanel.items) {
        if (selectedSyncElement.value && selectedSyncElement.value.elementPartType) {
          return props.tabItem.tabPanel.items.filter((item: any) => item.elementPartType === selectedSyncElement.value.elementPartType);
        }
        return props.tabItem.tabPanel.items;
      }
      return [];
    });

    const handleRefresh = () => {
      emit('refresh');
    };

    const stopWatchers: any[] = [];

    const initActiveFieldGroupIndex = (items: any[]) => {
      activeFieldGroupIndex.value = items.reduce((acc: number[], item, index) => {
        if (index === 0) {
          acc.push(index);
        }
        return acc;
      }, []);
    };

    stopWatchers.push(watch([
      () => props.parserValues,
      () => props.tabItem,
    ],
    () => {
      if (props.tabItem.tabPanel) {
        activeFieldGroupIndex.value = props.tabItem.tabPanel.items.reduce((acc: number[], item, index) => {
          acc.push(index);
          return acc;
        }, []);
      }
    }));

    stopWatchers.push(watch(selectedSyncElement, (newValue, oldValue) => {
      if (props.tabItem.tabPanel && !oldValue) {
        initActiveFieldGroupIndex(props.tabItem.tabPanel.items);
      }
    }, { deep: true }));

    const enterEditSyncElement = () => {
      const elementType = TemplateStructureEnum.DESIGN;
      const name = props.headerTitle;

      store.dispatch('liveEditor/showSyncElementWarning', {
        elementId: selectedStructure.value.identifier,
        elementType,
        name,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        acceptCallback: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        rejectCallback: () => {},
      });
    };

    onMounted(() => {
      if (props.tabItem.tabPanel) {
        initActiveFieldGroupIndex(props.tabItem.tabPanel.items);
      }
    });

    onBeforeUnmount(() => {
      stopWatchers.forEach((stop) => stop());
    });

    return {
      activeFieldGroupIndex,
      tabItems,
      toggleFieldGroup,
      handleRefresh,
      t,
      enterEditSyncElement,
      selectedSyncElement,
    };
  },
});
</script>

<style lang="scss" scoped>
.tab-panel-label {
  font-size: 1.2rem;
  font-weight: 800;
  color: $montana;
  padding: 0.5rem 0.75rem 0.5rem 0.75rem;
  line-height: 1rem;
  margin-bottom: 1rem;
}
</style>
